/**
 * Shopify Detection Plugin
 *
 * Detects when SDK is running on a Shopify storefront and adjusts behavior:
 *
 * 1. Suppresses automatic page tracking (Web Pixel handles it on checkout)
 * 2. Captures Shopify-specific context (cart token, customer ID)
 * 3. Sets up identity bridging for attribution
 *
 * This plugin should be registered before any events are sent.
 */

import type { Plugin } from "./plugin.js";
import type { SegmentflowWeb } from "../web.js";

/**
 * Shopify global object interface
 * Available on all Shopify storefronts
 */
interface ShopifyGlobal {
  shop?: string;
  locale?: string;
  currency?: {
    active: string;
    rate: string;
  };
  theme?: {
    name: string;
    id: number;
    theme_store_id: number | null;
  };
  routes?: {
    root: string;
  };
  cdnHost?: string;
  designMode?: boolean;
}

/**
 * Shopify cart object interface
 */
interface ShopifyCart {
  token?: string;
  items?: Array<{
    id: number;
    product_id: number;
    variant_id: number;
    quantity: number;
  }>;
  item_count?: number;
  total_price?: number;
  currency?: string;
}

/**
 * Shopify customer object interface (if logged in)
 * Prefixed with underscore as currently unused but reserved for future use
 */
interface _ShopifyCustomer {
  id?: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  orders_count?: number;
  total_spent?: string;
}

/**
 * Extended window interface for Shopify globals
 */
interface ShopifyWindow extends Window {
  Shopify?: ShopifyGlobal;
  ShopifyAnalytics?: unknown;
  __st?: {
    a?: number; // Shop ID
    p?: string; // Page type
    cid?: number; // Customer ID
  };
}

/**
 * Shopify context captured by the plugin
 */
export interface ShopifyContext {
  isShopify: boolean;
  shopDomain?: string;
  locale?: string;
  currency?: string;
  cartToken?: string;
  customerId?: string;
  designMode?: boolean;
}

/**
 * Check if running on a Shopify storefront
 * Prefixed with underscore as currently unused but reserved for future use
 */
function _detectShopify(): boolean {
  if (typeof window === "undefined") return false;
  const win = window as ShopifyWindow;
  return typeof win.Shopify !== "undefined";
}

/**
 * Get Shopify context from window globals
 */
function getShopifyContext(): ShopifyContext {
  if (typeof window === "undefined") {
    return { isShopify: false };
  }

  const win = window as ShopifyWindow;

  if (!win.Shopify) {
    return { isShopify: false };
  }

  const shopify = win.Shopify;

  // Try to get customer ID from __st global
  const customerId = win.__st?.cid
    ? `shopify_${win.__st.cid}`
    : undefined;

  return {
    isShopify: true,
    shopDomain: shopify.shop,
    locale: shopify.locale,
    currency: shopify.currency?.active,
    designMode: shopify.designMode,
    customerId,
    // Cart token is captured separately when available
  };
}

/**
 * Attempt to get cart token from various sources
 */
async function getCartToken(): Promise<string | null> {
  if (typeof window === "undefined") return null;

  try {
    // Method 1: Fetch cart.json (most reliable)
    const response = await fetch("/cart.json", {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (response.ok) {
      const cart = (await response.json()) as ShopifyCart;
      return cart.token ?? null;
    }
  } catch {
    // Fetch failed, try other methods
  }

  // Method 2: Check for cart form with token input
  const tokenInput = document.querySelector<HTMLInputElement>(
    'form[action*="/cart"] input[name="cart_token"], input[name="checkout[cart_token]"]',
  );
  if (tokenInput?.value) {
    return tokenInput.value;
  }

  return null;
}

/**
 * Shopify Plugin Implementation
 */
export class ShopifyPlugin implements Plugin {
  name = "shopify";

  private context: ShopifyContext = { isShopify: false };
  private cartToken: string | null = null;

  /**
   * Load plugin and set up Shopify integration
   */
  async load(_analytics: SegmentflowWeb): Promise<void> {
    // Detect Shopify environment
    this.context = getShopifyContext();

    if (!this.context.isShopify) {
      return; // Not a Shopify store, nothing to do
    }

    // Skip tracking in design mode (theme editor)
    if (this.context.designMode) {
      console.debug("[Segmentflow] Design mode detected, tracking limited");
    }

    // Get cart token for identity bridging
    this.cartToken = await getCartToken();

    console.debug("[Segmentflow] Shopify detected", {
      shop: this.context.shopDomain,
      cartToken: this.cartToken ? "present" : "absent",
      customerId: this.context.customerId,
    });
  }

  /**
   * Check if running on Shopify
   */
  isShopify(): boolean {
    return this.context.isShopify;
  }

  /**
   * Get captured Shopify context
   */
  getContext(): ShopifyContext {
    return { ...this.context, cartToken: this.cartToken ?? undefined };
  }

  /**
   * Get cart token for identity bridging
   */
  getCartToken(): string | null {
    return this.cartToken;
  }

  /**
   * Enrich track events with Shopify context
   */
  track(event: { context?: Record<string, unknown> }): void {
    if (!this.context.isShopify || !event.context) return;

    event.context.shopify = {
      shop_domain: this.context.shopDomain,
      locale: this.context.locale,
      currency: this.context.currency,
      cart_token: this.cartToken,
      customer_id: this.context.customerId,
    };
  }

  /**
   * Enrich identify events with Shopify context
   */
  identify(event: {
    userId?: string;
    traits?: Record<string, unknown>;
    context?: Record<string, unknown>;
  }): void {
    if (!this.context.isShopify || !event.context) return;

    // Add Shopify context
    event.context.shopify = {
      shop_domain: this.context.shopDomain,
      cart_token: this.cartToken,
    };

    // If this is a Shopify customer and we have their ID, add it to traits
    if (this.context.customerId && event.traits) {
      event.traits.shopify_customer_id = this.context.customerId;
    }
  }

  /**
   * Enrich page events with Shopify context
   */
  page(event: { context?: Record<string, unknown> }): void {
    if (!this.context.isShopify || !event.context) return;

    event.context.shopify = {
      shop_domain: this.context.shopDomain,
      locale: this.context.locale,
      currency: this.context.currency,
      cart_token: this.cartToken,
    };
  }
}

/**
 * Create Shopify plugin instance
 */
export function createShopifyPlugin(): Plugin {
  return new ShopifyPlugin();
}

export default createShopifyPlugin;
